import { useEffect, useMemo } from "react";
import {
  Badge,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormikProps, useFormikContext } from "formik";
import { orderBy } from "lodash";

import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";
import { CnvToggleStatus, IDesign, Workflow } from "types/design";
import { IDesignDetail } from "types/designDetail";
import { ICustomPanelForm } from "types/form";
import Footnotes from "./Footnotes";
import Switch from "./Swtich";
import GroupRow from "./TableRow";

const useStyles = makeStyles((theme: Theme) => ({
  tableContainer: {
    background: "#FFF",
  },
  specialModule: {
    padding: 16,
    "& > h6": {
      fontWeight: 400,
      fontSize: 12,
      lineHeight: 1.5,
      color: colors.nero[300],
    },
  },
  caption: {
    padding: "0 !important",
    color: "inherit !important",
  },
  header: {
    "& > th.MuiTableCell-head": {
      backgroundColor: "#FFF",
      padding: "11px 13px 11px 16px",
      fontSize: 12,
      lineHeight: "18px",
      letterSpacing: 0.5,
      fontWeight: 600,
      borderBottom: "none",
      whiteSpace: "nowrap",
    },
  },
  badge: {
    marginTop: 16,
    marginLeft: 6,
  },
  noResults: {
    borderBottom: "none",
    "& > h5": {
      color: colors.grey[300],
    },
  },
}));

const SpecialModuleRow = ({
  hasMsi = false,
  hasTmb = false,
}: {
  hasMsi?: boolean;
  hasTmb?: boolean;
}) => {
  const classes = useStyles();
  const { catalog, isCustomPanel } = useCatalogDetail();
  const { values, setFieldValue }: FormikProps<ICustomPanelForm> =
    useFormikContext();
  const isImmutable: boolean = useMemo(() => {
    return (
      (catalog as IDesign)[hasMsi ? "msiToggle" : "snpIdToggle"] ===
      CnvToggleStatus.IMMUTABLE_YES
    );
  }, [catalog, hasMsi]);

  useEffect(() => {
    // Initialize this value to false only for catalog panel customization
    if (!isCustomPanel) {
      setFieldValue(hasMsi ? "msi" : "snpId", false);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isCustomPanel]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      my={2}
      className={`default-bg ${classes.specialModule}`}
    >
      <Typography variant="subtitle2">
        {hasTmb
          ? "Tumor Mutational Burden (TMB)"
          : hasMsi
          ? "Microsatellite Instability (MSI)"
          : "SNPID for Sample Tracking (SNPs can be used in combination to uniquely tag and track samples over time)"}
      </Typography>
      {!hasTmb && (
        <Switch
          checked={
            isImmutable ? true : Boolean(hasMsi ? values.msi : values.snpId)
          }
          disabled={isImmutable}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFieldValue(hasMsi ? "msi" : "snpId", event.target.checked)
          }
        />
      )}
    </Box>
  );
};

const CustomTable = ({ workflow }: { workflow: Workflow }) => {
  let { catalog, tmbToggle, newDetails, diseaseType, isCustomPanel } =
    useCatalogDetail();
  catalog = catalog as IDesign;
  const classes = useStyles();

  return (
    <TableContainer className={`${classes.tableContainer} custom-table`}>
      {workflow === Workflow.VARITANT &&
        isCustomPanel &&
        "hasTmb" in catalog &&
        tmbToggle && <SpecialModuleRow hasTmb />}
      {["Solid Tumor"].includes(diseaseType?.name || "") &&
        workflow === Workflow.VARITANT &&
        (isCustomPanel
          ? "hasMsi" in catalog &&
            [CnvToggleStatus.IMMUTABLE_YES, CnvToggleStatus.MUTABLE].includes(
              catalog.msiToggle as CnvToggleStatus
            )
          : !catalog.hasMsi) && <SpecialModuleRow hasMsi />}
      {["Solid Tumor", "Blood Cancers"].includes(diseaseType?.name || "") &&
        workflow === Workflow.VARITANT &&
        (isCustomPanel
          ? "hasSnpId" in catalog &&
            [CnvToggleStatus.IMMUTABLE_YES, CnvToggleStatus.MUTABLE].includes(
              catalog.snpIdToggle as CnvToggleStatus
            )
          : !catalog.hasSnpId) && <SpecialModuleRow />}
      <Table stickyHeader>
        <caption className={classes.caption}>
          <Footnotes
            workflow={workflow}
            designType={catalog.designType}
            isOnDetail={false}
          />
        </caption>
        <TableHead>
          <TableRow className={classes.header}>
            <TableCell>Gene</TableCell>
            <TableCell>Accession</TableCell>
            <TableCell>
              <Badge
                badgeContent={<Link className={classes.badge}>*</Link>}
                invisible={workflow !== Workflow.FUSION}
              >
                Exon
              </Badge>
            </TableCell>
            {workflow === Workflow.FUSION && (
              <>
                <TableCell>Assay Type</TableCell>
                <TableCell>
                  Description<Link>†</Link>
                </TableCell>
              </>
            )}
            {workflow !== Workflow.FUSION && (
              <TableCell>
                <Badge badgeContent={<Link className={classes.badge}>*</Link>}>
                  CNV
                </Badge>
              </TableCell>
            )}
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {newDetails.size === 0 && (
            <TableRow>
              <TableCell
                colSpan={5}
                align="center"
                className={classes.noResults}
              >
                <Typography variant="h5">
                  Add new design by searching for targets
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {newDetails.size > 0 &&
            Array.from(newDetails, (data: any) => {
              const designId: number = data[0];
              const designDetails: IDesignDetail[] = data[1]?.designDetails;
              const cnvToggle: boolean = data[1].cnvToggle;
              const cnv: boolean = data[1].cnv;

              return (
                <GroupRow
                  key={designId} // Design Id
                  detailGroup={orderBy(designDetails, ["gene"], ["asc"])}
                  workflow={workflow}
                  cnv={cnv}
                  cnvToggle={cnvToggle}
                  isOnDetail={designId}
                />
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomTable;
